import React, { useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Stack,
    Chip,
    Card,
    CardContent,
    CardActions,
    Divider,
    Box,
    useTheme,
    useMediaQuery,
    IconButton,
    Tooltip,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';

const BranchList = ({
    branches,
    contacts,
    onUpdateClick,
    onDeleteClick,
    onViewDocuments,
    onViewContacts,
    isAdmin,
    userRole,
    userContactId,
}) => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const contactMap = useMemo(() => {
        const map = new Map();
        contacts.forEach(contact => {
            if (contact?.contactId) {
                map.set(contact.contactId, contact.contactName || contact.email || contact.contactId);
            }
        });
        return map;
    }, [contacts]);

    if (!branches || branches.length === 0) {
        return <Typography variant="body1">No branches found for the selected business.</Typography>;
    }

    const resolveSupervisorNames = (ids = []) => ids.map(id => contactMap.get(id) || id);

    const buildPermissions = (branch) => {
        const canUpdate = (() => {
            if (isAdmin) return true;
            if (userRole === 'Business Owner') return true;
            if (userRole === 'Supervisor') {
                return Array.isArray(branch.supervisorIds) && branch.supervisorIds.includes(userContactId);
            }
            return false;
        })();
        const canDelete = isAdmin || userRole === 'Business Owner';
        return { canUpdate, canDelete };
    };

    const renderActions = (branch, canUpdate, canDelete, justify = 'flex-end') => {
        const iconButtonSx = {
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
            transition: 'all 0.2s ease',
            '&:hover': {
                bgcolor: theme.palette.action.hover,
            },
        };
        const canViewDocuments = typeof onViewDocuments === 'function';
        const canViewContacts = typeof onViewContacts === 'function';

        return (
            <Stack direction="row" spacing={1} justifyContent={justify}>
                <Tooltip title={canViewContacts ? 'View contacts' : 'Contacts unavailable'}>
                    <span>
                        <IconButton
                            color="primary"
                            size="small"
                            sx={iconButtonSx}
                            onClick={() => onViewContacts?.(branch)}
                            disabled={!canViewContacts}
                        >
                            <ContactsOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={canViewDocuments ? 'View documents' : 'Documents unavailable'}>
                    <span>
                        <IconButton
                            color="primary"
                            size="small"
                            sx={iconButtonSx}
                            onClick={() => onViewDocuments?.(branch)}
                            disabled={!canViewDocuments}
                        >
                            <DescriptionOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={canUpdate ? 'Edit branch' : 'You do not have permission to edit'}>
                    <span>
                        <IconButton
                            size="small"
                            sx={iconButtonSx}
                            onClick={() => onUpdateClick(branch)}
                            disabled={!canUpdate}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={canDelete ? 'Remove branch' : 'You do not have permission to delete'}>
                    <span>
                        <IconButton
                            size="small"
                            color="error"
                            sx={iconButtonSx}
                            onClick={() => onDeleteClick(branch)}
                            disabled={!canDelete}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        );
    };

    if (!isDesktop) {
        return (
            <Stack spacing={2}>
                {branches.map(branch => {
                    const supervisorNames = resolveSupervisorNames(branch.supervisorIds);
                    const { canUpdate, canDelete } = buildPermissions(branch);

                    return (
                        <Card key={branch.branchId} variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {branch.nickname || branch.address}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    FSSAI License: {branch.fssaiLicenseNumber || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                                    {branch.address}
                                </Typography>
                                <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                    <InfoRow label="State" value={branch.state || 'N/A'} />
                                    <InfoRow label="District" value={branch.district || 'N/A'} />
                                </Stack>
                                <Divider sx={{ my: 1.5 }} />
                                <Stack spacing={1.5}>
                                    <InfoRow label="Phone" value={branch.phoneNumber} />
                                    <InfoRow label="Email" value={branch.email || 'N/A'} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Supervisors</Typography>
                                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                                            {supervisorNames.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">None</Typography>
                                            ) : (
                                                supervisorNames.map(name => <Chip key={name} label={name} size="small" />)
                                            )}
                                        </Stack>
                                    </Box>
                                </Stack>
                            </CardContent>
                            <CardActions sx={{ px: 2, pb: 2 }}>
                                {renderActions(branch, canUpdate, canDelete, 'flex-start')}
                            </CardActions>
                        </Card>
                    );
                })}
            </Stack>
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined">
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Nickname</TableCell>
                        <TableCell>FSSAI License</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>District</TableCell>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Supervisors</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {branches.map(branch => {
                        const supervisorNames = resolveSupervisorNames(branch.supervisorIds);
                        const { canUpdate, canDelete } = buildPermissions(branch);

                        return (
                            <TableRow key={branch.branchId}>
                                <TableCell>{branch.nickname}</TableCell>
                                <TableCell>{branch.fssaiLicenseNumber || 'N/A'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-line' }}>{branch.address}</TableCell>
                                <TableCell>{branch.state || 'N/A'}</TableCell>
                                <TableCell>{branch.district || 'N/A'}</TableCell>
                                <TableCell>{branch.phoneNumber}</TableCell>
                                <TableCell>{branch.email || 'N/A'}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                        {supervisorNames.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">None</Typography>
                                        ) : (
                                            supervisorNames.map(name => <Chip key={name} label={name} size="small" />)
                                        )}
                                    </Stack>
                                </TableCell>
                                <TableCell align="right">
                                {renderActions(branch, canUpdate, canDelete)}
                            </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const InfoRow = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
    </Box>
);

export default BranchList;
